/**
 * ISO 3166-1 English country names via `i18n-iso-countries`, plus a local
 * `OTHER` sentinel for places that aren't in the standard list.
 *
 * Stored values are alpha-2 codes (e.g. `US`) or `OTHER` — never free-form names,
 * so filters and exports stay stable if the English label changes in a package update.
 */
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json" with { type: "json" };

countries.registerLocale(enLocale);

/** Non-ISO escape hatch stored alongside real alpha-2 codes. */
export const COUNTRY_OTHER = "OTHER" as const;

export type CountryCode = string;

export interface CountryOption {
  code: CountryCode;
  name: string;
}

let cachedOptions: CountryOption[] | null = null;

function isAlpha2(code: string): boolean {
  return Object.hasOwn(countries.getAlpha2Codes(), code);
}

/** Official English names, A→Z, then Other at the end. */
export function countryOptions(): readonly CountryOption[] {
  if (cachedOptions) return cachedOptions;

  const names = countries.getNames("en", { select: "official" });
  const list: CountryOption[] = Object.entries(names)
    .map(([code, name]) => ({ code, name }))
    .sort((a, b) => a.name.localeCompare(b.name, "en"));
  list.push({ code: COUNTRY_OTHER, name: "Other" });
  cachedOptions = list;
  return list;
}

/** True for ISO alpha-2 or our Other sentinel. */
export function isCountryFieldValue(code: string): boolean {
  return code === COUNTRY_OTHER || isAlpha2(code);
}

/** English label for a stored code; unknown codes fall back to the raw value. */
export function countryLabel(code: string): string {
  if (code === COUNTRY_OTHER) return "Other";
  return countries.getName(code, "en") ?? code;
}
